import React, { useState } from "react";
import { Grid, Paper, Box, List, ListItem, ListItemAvatar, Avatar, ListItemText, ListItemSecondaryAction, Divider } from '@mui/material';
import Colors from "../../../../constants/Colors";
import { AccessTimeOutlined, CloudOutlined, Edit, LocationOn, PersonOutline, ReceiptOutlined, Store, Web } from "@mui/icons-material";
import IconTextSection from "../../../common/IconTextSection";
import { withRouter } from "react-router";
import StyledSubHeader from "../../../common/StyledSubHeader";
import { ContainedButton, TooltipIconButton } from "../../../common/StyledButtons";
import { CARD_HEADER_STYLE, ICON_TITLE_TEXT_STYLE } from "../../../../constants/Styles";
import { getSiteById } from "../../../../selectors/SiteSelectors";
import StyledChip from "../../../common/StyledChip";
import { getIsUserAdmin } from "../../../../selectors/UserSelectors";
import EditSiteAccessModal from "../../modals/EditSiteAccessModal";
import StyledPage from "../../../common/StyledPage";
import StyledSection from "../../../common/StyledSection";

const SiteAccountDetails = ({ history, match }) => {

  const [showEditAccessModal, setShowEditAccessModal] = useState(false);

  const siteId = match.params.site_id;
  const site = getSiteById(siteId);

  const prepareActions = () => {
    const tools = [
      {
        name: "Billing",
        icon: <ReceiptOutlined sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
        description: 'See invoices and billing preferences for your business.',
        link: `/dashboard/site/${siteId}/account/billing`
      }
    ]
    return tools;
  }

  const renderWebsite = () => {
    if (site && site.site_service_modules && site.site_service_modules.website && site.site_service_modules.website.domain )
    return <a style={{ color: Colors.primaryColor }} href={`https://${site.site_service_modules.website.domain.url}`} target='_blank'>{site.site_service_modules.website.domain.url}</a>
    else return <i>Not Available</i>
  }

  const renderQuickActionsSection = () => (
    <StyledSection
            title="Actions"
        >  
        <Grid container item xs={12} spacing={1}>
            {prepareActions().map((action) => (
          <Grid item xs={12} key={action.name}>
              <Paper
              variant="outlined"
              onClick={() =>  history.push(action.link)}
              sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: '100%',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      transform: 'scale(1.02)',
                    },
              }}
              >
              {/* Left: Icon + Text */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                  <Box>{action.icon}</Box>
                  <Box>
                  <div style={CARD_HEADER_STYLE}>{action.name}</div>
                  <p style={{ margin: 0 }}>{action.description}</p>
                  </Box>
              </Box>
              </Paper>
          </Grid>))}
        </Grid>
    </StyledSection>
)

const renderAccessContent = () => (
  <StyledSection
    title="User Access"
    rightAdornment={
      (site.site_security.is_primary_user || getIsUserAdmin()) && (
        <TooltipIconButton
          text="Manage User Access"
          icon={<Edit sx={{ fontSize: '18px' }} />}
          onClick={() => setShowEditAccessModal(true)}
        />
      )
    }
  >
    <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px', borderRadius: '8px', }}>
      <List style={{ flex: 1 }}>
        {site && site.site_security.accessible_users.map((user, index) => (
          <ListItem
            dense
            key={user.user_id || user.email}
            sx={{
              backgroundColor: '#fff',
              marginBottom: '5px',
              borderBottom: index !== site.site_security.accessible_users.length - 1 ? '1px solid #e0e0e0' : 'none',
            }}
          >
            <ListItemAvatar>
              <Avatar>
                <PersonOutline />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={`${user.first_name} ${user.last_name}`}
              secondary={user.email}
            />
            {user.is_primary_user && (
              <ListItemSecondaryAction>
                <StyledChip text="Primary" />
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>
    </Paper>
  </StyledSection>
);

  const renderAddress = () => {
    const addr = site.site_details.site_address;
  
    if (!addr || !addr.address_line_1) {
      return <i>Not Provided</i>;
    }
  
    const line1 = addr.address_line_1 || '';
    const line2 = addr.address_line_2 ? ` ${addr.address_line_2}` : '';
    const cityStateZip = [addr.city, addr.state, addr.zipcode].filter(Boolean).join(' ');
  
    return (
      <span>
        {line1}{line2}
        {cityStateZip && `, ${cityStateZip}`}
      </span>
    );
  };

  const renderOverviewSection = () => (
    <StyledSection
    title="Business Details"
    >
    <Box
      sx={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: 2,
      }}
    >
      <IconTextSection
        icon={<Store sx={{ fontSize: '24px' }} />}
        primaryTextStyle={{ ...ICON_TITLE_TEXT_STYLE }}
        primaryText="Business Name"
        secondaryText={`${site.site_details.site_name}${site.site_details.site_structure ? `, ${site.site_details.site_structure}` : ''}`}
      />
      <Divider sx={{ my: 2 }} />
      <IconTextSection
        icon={<Web sx={{ fontSize: '24px' }} />}
        primaryTextStyle={{ ...ICON_TITLE_TEXT_STYLE }}
        primaryText="Primary Domain"
        secondaryText={renderWebsite()}
      />
      <Divider sx={{ my: 2 }} />
      <IconTextSection
        icon={<LocationOn sx={{ fontSize: '24px' }} />}
        primaryTextStyle={{ ...ICON_TITLE_TEXT_STYLE }}
        primaryText="Business Address"
        secondaryText={renderAddress()}
      />
    </Box>
      

    </StyledSection>
  )

  return (
    <>
     <StyledPage>
        <Grid container >
            <Grid item xs={12}>
              <StyledSubHeader title="Account" />
            </Grid>

            <Grid item xs={12}>
              {renderOverviewSection()}
            </Grid>
            <Grid item xs={12}>
            {renderAccessContent()}
            </Grid>
            <Grid item xs={12}>
              {renderQuickActionsSection()}
            </Grid>
      </Grid>
    </StyledPage>
    <EditSiteAccessModal
        open={showEditAccessModal}
        onClose={() => setShowEditAccessModal(false)}
        site={site}
      />
    </>
  )
}

export default withRouter(SiteAccountDetails);
